import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import type { TextInputSize } from "@/shared/foundation/primitives";

import type { LabeledOption } from "../FilterDefinition";
import type { ListMemberLabels } from "../operators";
import { suggestionOptions } from "../suggestionOptions";
import type { SuggestionsProvider } from "../suggestions";

import { type ListValueCodec, stringValueCodec } from "./listValueCodecs";
import {
  type CustomValueOptions,
  OptionsValueInput,
} from "./OptionsValueInput";

interface SuggestionsValueInputBaseProps<T> {
  /**
   * The selected values, in the order they were picked. Values that format to
   * the same text show as one row. "Not set" is never one of them (see
   * `includeNull`).
   */
  value: T[];
  /**
   * Called with the next selected values, parsed by the codec and with
   * duplicates removed. Not called when a change reads back as the same
   * values, unless it also adds or removes "Not set" (Clear all, Select
   * all), which passes its next state as `includeNull` in the same call
   * rather than calling `onIncludeNullChange` too (see `OptionsValueInput`'s
   * `onChange`).
   */
  onChange: (next: T[], includeNull?: boolean) => void;
  /**
   * The comparators each member stands for: drawn before every value's row,
   * and as the whole of the "Not set" row (see `listMemberLabels`).
   */
  memberLabels: ListMemberLabels;
  /** Take focus on mount. @default true */
  autoFocus?: boolean;
  /** Marks the input invalid, for an error shown beside it. */
  invalid?: boolean;
  /** Field size, on the `TextInput` scale. @default "md" */
  size?: TextInputSize;
  /**
   * Values to suggest. Hints only: typed text stays a value. A new source
   * should remount the input (key it by field), so rows fetched for the old
   * one don't linger.
   */
  suggestions?: SuggestionsProvider;
  /**
   * Whether the field's value can be absent, which is what makes "Not set" a
   * member worth offering. @default false
   */
  nullable?: boolean;
  /** Whether "Not set" is a member of the selection. @default false */
  includeNull?: boolean;
  /**
   * Called when the author adds or removes the "Not set" member. Without it,
   * "Not set" is shown when included but never offered.
   */
  onIncludeNullChange?: (includeNull: boolean) => void;
  /**
   * Called with whether the typed text could be added as a value (see
   * `OptionsValueInput`'s `onDraftValidChange`).
   */
  onDraftValidChange?: (valid: boolean) => void;
}

/**
 * Strings need no codec. Any other value type must bring one, since the
 * picker's rows and typed text are text.
 */
type SuggestionsValueInputProps<T> = SuggestionsValueInputBaseProps<T> &
  ([T] extends [string]
    ? { codec?: ListValueCodec<T> }
    : { codec: ListValueCodec<T> });

/**
 * The values of a list filter (`ANY_OF` / `NONE_OF`) of typed values (strings,
 * numbers): the enum picker (`OptionsValueInput`) over the field's suggestions,
 * plus whatever the author types.
 *
 * Values are listed as rows, selected ones first, each after the comparator it
 * stands for ("equals nightly"); the field's suggestions follow, then the typed
 * text as a new value. Enter adds the typed text, as does focus leaving the
 * picker.
 *
 * Values travel through the picker as their text (`codec.format`) and are read
 * back with `codec.parse`, so two spellings of one value ("3" and "3.0")
 * collapse to the first, and text the codec rejects is never added.
 *
 * "Not set" is a member of its own, never a value: a row ahead of the rest,
 * ticked while included, listed on a nullable field while included or while
 * the suggestion source doesn't report that every entity has a value.
 */
export function SuggestionsValueInput<T = string>({
  value,
  onChange,
  memberLabels,
  codec: givenCodec,
  autoFocus = true,
  invalid = false,
  size = "md",
  suggestions: provider,
  nullable = false,
  includeNull = false,
  onIncludeNullChange,
  onDraftValidChange,
}: SuggestionsValueInputProps<T>) {
  // Only a string list may leave the codec out (see the props type).
  const codec =
    givenCodec ?? (stringValueCodec as unknown as ListValueCodec<T>);
  const queryClient = useQueryClient();
  const source = React.useMemo(
    () => suggestionOptions(provider, queryClient),
    [provider, queryClient],
  );

  const selected = React.useMemo(() => {
    const seen = new Set<string>();
    const options: LabeledOption[] = [];
    for (const item of value) {
      const text = codec.format(item);
      if (!seen.has(text)) {
        seen.add(text);
        options.push({ label: text, value: text });
      }
    }
    return options;
  }, [value, codec]);

  // Each row's text is read back as a value; a selection that reads back as
  // the same values changes nothing, unless "Not set" changes with it.
  const handleChange = (next: LabeledOption[], nextIncludeNull?: boolean) => {
    const values: T[] = [];
    const seen = new Set<string>();
    for (const option of next) {
      const parsed = codec.parse(option.value);
      if (parsed === undefined) {
        continue;
      }
      const text = codec.format(parsed);
      if (!seen.has(text)) {
        seen.add(text);
        values.push(parsed);
      }
    }
    const unchanged =
      values.length === selected.length &&
      values.every(
        (item, index) => codec.format(item) === selected[index].value,
      );
    if (nextIncludeNull !== undefined) {
      onChange(values, nextIncludeNull);
    } else if (!unchanged) {
      onChange(values);
    }
  };

  const customValue = React.useMemo(
    (): CustomValueOptions => ({
      normalize: (text) => {
        const parsed = codec.parse(text);
        return parsed === undefined ? undefined : codec.format(parsed);
      },
    }),
    [codec],
  );

  // An included "Not set" stays listed, ticked, so it can be unticked even
  // where it would no longer be offered.
  const offersNotSet = nullable && onIncludeNullChange !== undefined;
  const notSet =
    offersNotSet || includeNull
      ? {
          selected: includeNull,
          onToggle: () => onIncludeNullChange?.(!includeNull),
        }
      : undefined;

  return (
    <OptionsValueInput
      // A new source (a fetch provider's key changes with what it fetches)
      // starts over: its complete-set latch and search belong to the old one.
      key={source.optionsKey ?? "static"}
      // Both list comparators pick values the same way; the operator says
      // which one the filter applies.
      comparator="ANY_OF"
      value={selected}
      onChange={handleChange}
      {...source}
      memberLabels={memberLabels}
      notSet={notSet}
      customValue={customValue}
      onDraftValidChange={onDraftValidChange}
      autoFocus={autoFocus}
      size={size}
      invalid={invalid}
    />
  );
}
